import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { getRealizadoErros, resolverRealizadoErros } from "../../api";
import { GUIA, acoesDoErro, ondeEsta } from "../../components/gestaoRealizado/guiaErros";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import {
  AvisoErro, Badge, BarraFiltros, Card, Carregando, Seletor, Vazio, botaoPequeno, estiloTabela, formatData, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Log de erros do realizado: tudo o que não casou na carga dos CSVs, no formulário, na
// sincronização com a planilha ou com o OMIE (incl. o histórico de notas alteradas lá). Nada é corrigido em silêncio — o financeiro trata e marca
// como resolvido (um erro resolvido não volta nas próximas cargas).

const TIPOS = {
  PROJETO_NAO_MAPEADO: "Projeto sem de-para",
  TIPO_RECEITA_VAZIO: "Sem Backlog/Nova Venda",
  SALDO_DIVERGENTE: "Saldo divergente",
  NF_NAO_IDENTIFICADA: "NF não identificada",
  CLIENTE_SEM_CNPJ: "Cliente sem CNPJ",
  COMPETENCIA_INVALIDA: "Competência inválida",
  VALOR_ZERO: "Valor zero",
  VALOR_INVALIDO: "Valor inválido",
  DATA_INVALIDA: "Data inválida",
  EMPRESA_INVALIDA: "Empresa inválida",
  NF_DUPLICADA: "NF duplicada",
  SYNC_PLANILHA: "Falha na planilha",
  SOLICITACAO_SEM_SALDO: "Solicitação sem saldo",
  SOLICITACAO_NAO_ENCONTRADA: "Só na aba Emissão",
  NF_SEM_SOLICITACAO: "NF sem solicitação",
  NF_CASAMENTO_AMBIGUO: "NF com vínculo a conferir",
  NF_NAO_ENCONTRADA_OMIE: "NF não está no OMIE",
  NF_EXCLUIDA_OMIE: "NF excluída no OMIE",
  NF_ALTERADA_OMIE: "NF alterada no OMIE",
  NF_CANCELADA_LIBERADA: "NF cancelada · solicitação liberada",
  NF_VALOR_DIVERGENTE: "NF com valor diferente",
  NFE_OFICIAL_DESALINHADA: "Nfe oficial desalinhada",
  REGISTRO_TESTE: "Registro de teste",
  CATEGORIA_SEM_CODIGO: "Despesa: categoria sem código",
  CATEGORIA_NAO_ENCONTRADA: "Despesa: categoria desconhecida",
  LANCAMENTO_SEM_DEPARTAMENTO: "Despesa sem departamento",
  DEPARTAMENTO_SEM_DEPARA: "Departamento sem de-para",
};
const ORIGENS = {
  CARGA_RESPOSTAS: "CSV Respostas", CARGA_EMISSAO_COMP: "CSV Emissão de Nota", CARGA_CONTROLE_COMP: "CSV Controle comp.",
  CARGA_NFE_OFICIAL: "CSV Nfe oficial", SYNC_OMIE: "OMIE", SYNC_OMIE_HISTORICO: "OMIE · histórico",
  FORMULARIO: "Formulário", SYNC_PLANILHA: "Sincronização", CONCILIACAO: "Conciliação",
  SYNC_OMIE_DESPESAS: "OMIE · despesas",
};

export default function LogErrosRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const [situacao, setSituacao] = useState("pendentes");
  const [tipo, setTipo] = useState("");
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [expandidos, setExpandidos] = useState(() => new Set());
  const [erroAcao, setErroAcao] = useState("");
  const navigate = useNavigate();
  const { dados, carregando, erro, recarregar } = useCarregar(
    () => getRealizadoErros({ situacao, tipo }), [situacao, tipo],
  );
  const T = estiloTabela(C);
  const erros = dados?.erros || [];
  const contagem = dados?.contagemPendentes || {};

  const alternar = (setter, id) => setter((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const marcar = async (valor) => {
    try {
      await resolverRealizadoErros([...selecionados], valor);
      setSelecionados(new Set());
      recarregar();
    } catch (e) {
      setErroAcao(e.message);
    }
  };

  return (
    <RealizadoLayout titulo="Log de erros" isLight={isLight} setIsLight={setIsLight}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {Object.entries(contagem).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
          <button key={t} onClick={() => setTipo(tipo === t ? "" : t)} style={{
            border: `1px solid ${tipo === t ? C.brand : C.border}`, background: tipo === t ? C.bgActive : C.card, color: C.text,
            borderRadius: 999, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit",
          }}>
            {TIPOS[t] || t} <b style={{ color: C.danger }}>{n}</b>
          </button>
        ))}
      </div>
      <BarraFiltros>
        <Seletor C={C} rotulo="Situação" value={situacao} onChange={setSituacao} opcoes={[
          { value: "pendentes", label: "Pendentes" }, { value: "resolvidos", label: "Resolvidos" }, { value: "todos", label: "Todos" },
        ]} />
        <Seletor C={C} rotulo="Tipo" value={tipo} onChange={setTipo} opcoes={[{ value: "", label: "Todos" }, ...Object.entries(TIPOS).map(([v, l]) => ({ value: v, label: l }))]} />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button style={botaoPequeno(C.success)} disabled={!selecionados.size} onClick={() => marcar(true)}>Marcar {selecionados.size || ""} como resolvido</button>
          <button style={botaoPequeno(C.muted, true)} disabled={!selecionados.size} onClick={() => marcar(false)}>Reabrir</button>
        </div>
      </BarraFiltros>
      <AvisoErro C={C} onClose={() => setErroAcao("")}>{erro || erroAcao}</AvisoErro>
      {tipo && GUIA[tipo] && (
        <div style={{ border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.brand}`, background: C.card, borderRadius: 8, padding: "12px 16px" }}>
          <GuiaSolucao C={C} guia={GUIA[tipo]} acoes={GUIA[tipo].acao ? [GUIA[tipo].acao] : []} navigate={navigate} />
        </div>
      )}

      {carregando && !dados ? <Carregando C={C} /> : (
        <Card C={C} titulo={`${erros.length} registro(s)`}>
          {erros.length === 0 ? <Vazio C={C}>Nenhum erro com esses filtros.</Vazio> : (
            <div style={{ ...T.wrap, maxHeight: "65vh" }}>
              <table style={{ ...T.table, minWidth: 1100 }}>
                <thead><tr>
                  <th style={T.th}>
                    <input type="checkbox" aria-label="Selecionar todos" checked={selecionados.size === erros.length} onChange={(e) => setSelecionados(e.target.checked ? new Set(erros.map((x) => x.id)) : new Set())} />
                  </th>
                  <th style={T.th} />
                  <th style={T.th}>Tipo</th>
                  <th style={T.th}>Origem</th>
                  <th style={T.th}>Referência</th>
                  <th style={T.th}>Mensagem</th>
                  <th style={T.th}>Como resolver</th>
                  <th style={T.th}>Data</th>
                </tr></thead>
                <tbody>
                  {erros.map((e) => (
                    <Fragment key={e.id}>
                      <tr>
                        <td style={T.td}><input type="checkbox" aria-label={`Selecionar erro ${e.id}`} checked={selecionados.has(e.id)} onChange={() => alternar(setSelecionados, e.id)} /></td>
                        <td style={{ ...T.td, cursor: "pointer", color: C.muted }} onClick={() => alternar(setExpandidos, e.id)}>
                          {expandidos.has(e.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                        <td style={T.td}><Badge cor={e.resolvido ? C.success : C.danger}>{TIPOS[e.tipo] || e.tipo}</Badge></td>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>{ORIGENS[e.origem] || e.origem}</td>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>{e.referencia}</td>
                        <td style={T.td}>
                          {e.mensagem}{e.resolvidoPor && <span style={{ color: C.muted }}> · resolvido por {e.resolvidoPor}</span>}
                          {GUIA[e.tipo] && <div style={{ color: C.muted, fontSize: 11, marginTop: 3 }}>{GUIA[e.tipo].oQue}</div>}
                        </td>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                            {acoesDoErro(e).map((a) => (
                              <button key={a.path} style={botaoPequeno(C.brand, true)} onClick={() => navigate(a.path)}>
                                {a.rotulo} <ArrowRight size={12} style={{ verticalAlign: "middle" }} />
                              </button>
                            ))}
                            <button onClick={() => alternar(setExpandidos, e.id)} style={{ background: "none", border: "none", padding: 0, color: C.brand, cursor: "pointer", fontSize: 11, fontFamily: "inherit", textDecoration: "underline" }}>
                              {expandidos.has(e.id) ? "Ocultar passo a passo" : "Ver passo a passo"}
                            </button>
                          </div>
                        </td>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>{formatData(e.criadoEm)}</td>
                      </tr>
                      {expandidos.has(e.id) && (
                        <tr>
                          <td colSpan={2} />
                          <td colSpan={6} style={{ ...T.td, background: C.bg }}>
                            {ondeEsta(e) && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>📍 {ondeEsta(e)}</div>}
                            {GUIA[e.tipo]
                              ? <GuiaSolucao C={C} guia={GUIA[e.tipo]} acoes={acoesDoErro(e)} navigate={navigate} />
                              : <div style={{ fontSize: 12, color: C.muted }}>Sem guia para este tipo. Confira o dado original e marque como resolvido após tratar.</div>}
                            <details style={{ marginTop: 10 }}>
                              <summary style={{ cursor: "pointer", fontSize: 11, color: C.muted }}>Dado original</summary>
                              <pre style={{ margin: "6px 0 0", fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word", color: C.text, maxHeight: 260, overflow: "auto" }}>
                                {e.dado ? JSON.stringify(e.dado, null, 2) : "Sem dado original."}
                              </pre>
                            </details>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </RealizadoLayout>
  );
}

// O que aconteceu → passo a passo → se sai do log sozinho ou precisa marcar como resolvido.
function GuiaSolucao({ C, guia, acoes, navigate }) {
  return (
    <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
      <div><b>O que aconteceu:</b> {guia.oQue}</div>
      <div style={{ marginTop: 6 }}><b>Como resolver:</b></div>
      <ol style={{ margin: "2px 0 0", paddingLeft: 20 }}>
        {guia.passos.map((p) => <li key={p}>{p}</li>)}
      </ol>
      <div style={{ marginTop: 6, color: C.muted }}>
        {guia.automatico ? `✔ ${guia.automatico}` : "✔ Depois de tratar, selecione o erro e clique em Marcar como resolvido."}
      </div>
      {acoes.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          {acoes.map((a) => (
            <button key={a.path} style={botaoPequeno(C.brand)} onClick={() => navigate(a.path)}>
              {a.rotulo} <ArrowRight size={12} style={{ verticalAlign: "middle" }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
